import React from 'react';
import { Box, Grid, TextField, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import DatePicker from 'react-datepicker';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { styled } from '@mui/material/styles';
import FormFieldLabel from '../components/FormFieldLabel';
import LocationMapPreview from '../components/LocationMapPreview';
import LocationAutocomplete from '../components/LocationAutocomplete';
import EventTimePicker from '../components/EventTimePicker';
import { formatDate, MOMENT_FORMATS } from '../../../../utils/dateFormat';
import {
  EVENT_FORMAT,
  EVENT_FORMAT_OPTIONS,
  isOnlineEventFormat,
  normalizeEventFormat,
} from '../eventFormatConstants';
import { PLACEHOLDER_COLOR, mergeFieldSx } from '../components/manageEventFieldStyles';
import { getMinSelectableDate } from '../manageEventForm';
import 'react-datepicker/dist/react-datepicker.css';

const datePickerWrapperSx = {
  width: '100%',
  '& .react-datepicker-wrapper': {
    width: '100%',
    display: 'block',
  },
};

const DateInput = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hasError',
})(({ theme, hasError }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  height: 56,
  padding: theme.spacing(0, 1.5),
  border: `1px solid ${hasError ? theme.palette.error.main : theme.palette.divider}`,
  borderRadius: 10,
  backgroundColor: theme.palette.background.paper,
  cursor: 'pointer',
  boxSizing: 'border-box',
  '&:hover': {
    borderColor: hasError ? theme.palette.error.main : theme.palette.text.disabled,
  },
}));

const FORMAT_ICONS = {
  [EVENT_FORMAT.OFFLINE]: <PlaceOutlinedIcon sx={{ fontSize: 18, mr: 0.75 }} />,
  [EVENT_FORMAT.ONLINE]: <VideocamOutlinedIcon sx={{ fontSize: 18, mr: 0.75 }} />,
};

const DateLocationStep = ({ form, errors = {}, onChange, onLocationPlaceSelected }) => {
  const minDate = getMinSelectableDate();
  const update = (field) => (e) => onChange(field, e.target.value);
  const locationError = errors.location || errors.latitude || errors.longitude;
  const isOnline = isOnlineEventFormat(form.eventFormat);

  const handleLocationInput = (value) => {
    onChange('location', value);
    onChange('latitude', '');
    onChange('longitude', '');
  };

  const handleEventFormatChange = (_, nextFormat) => {
    if (!nextFormat) return;

    const format = normalizeEventFormat(nextFormat);
    onChange('eventFormat', format);

    if (format === EVENT_FORMAT.ONLINE) {
      onChange('venueName', '');
      onChange('location', '');
      onChange('latitude', '');
      onChange('longitude', '');
      return;
    }

    onChange('onlineMeetingLink', '');
  };

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FormFieldLabel required>Date</FormFieldLabel>
          <Box sx={datePickerWrapperSx}>
            <DatePicker
              selected={form.eventDate}
              onChange={(date) => onChange('eventDate', date)}
              minDate={minDate}
              placeholderText="Add Date"
              customInput={
                <DateInput hasError={Boolean(errors.eventDate)}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: form.eventDate ? 'text.primary' : PLACEHOLDER_COLOR,
                      fontSize: '0.875rem',
                    }}
                  >
                    {form.eventDate ? formatDate(form.eventDate, MOMENT_FORMATS.DATE) : 'Add Date'}
                  </Typography>
                  <CalendarTodayOutlinedIcon sx={{ fontSize: 20, color: PLACEHOLDER_COLOR }} />
                </DateInput>
              }
            />
          </Box>
          {errors.eventDate && (
            <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.75, display: 'block' }}>
              {errors.eventDate}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <FormFieldLabel required>Time</FormFieldLabel>
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <EventTimePicker
                value={form.startTime}
                placeholder="Start"
                onChange={(time) => onChange('startTime', time)}
                error={errors.startTime}
                helperText={errors.startTime}
              />
            </Grid>
            <Grid item xs={6}>
              <EventTimePicker
                value={form.endTime}
                placeholder="End"
                onChange={(time) => onChange('endTime', time)}
                error={errors.endTime}
                helperText={errors.endTime}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <FormFieldLabel required>Event format</FormFieldLabel>
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={normalizeEventFormat(form.eventFormat)}
            onChange={handleEventFormatChange}
            sx={{ mb: 0.5 }}
          >
            {EVENT_FORMAT_OPTIONS.map((option) => (
              <ToggleButton
                key={option.value}
                value={option.value}
                sx={{ textTransform: 'none', fontWeight: 600, py: 1.25 }}
              >
                {FORMAT_ICONS[option.value]}
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {errors.eventFormat ? (
            <Typography variant="caption" sx={{ color: '#d32f2f', display: 'block' }}>
              {errors.eventFormat}
            </Typography>
          ) : null}
        </Grid>

        {isOnline ? (
          <Grid item xs={12}>
            <FormFieldLabel required>Online meeting link</FormFieldLabel>
            <TextField
              fullWidth
              placeholder="https://zoom.us/j/..."
              value={form.onlineMeetingLink}
              onChange={update('onlineMeetingLink')}
              error={Boolean(errors.onlineMeetingLink)}
              helperText={errors.onlineMeetingLink}
              sx={mergeFieldSx(Boolean(errors.onlineMeetingLink))}
            />
          </Grid>
        ) : (
          <>
            <Grid item xs={12} md={6}>
              <FormFieldLabel required>Venue Name</FormFieldLabel>
              <TextField
                fullWidth
                placeholder="Add Venue Name"
                value={form.venueName}
                onChange={update('venueName')}
                error={Boolean(errors.venueName)}
                helperText={errors.venueName}
                sx={mergeFieldSx(Boolean(errors.venueName))}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormFieldLabel required>Location</FormFieldLabel>
              <LocationAutocomplete
                value={form.location}
                onChange={handleLocationInput}
                onPlaceSelected={onLocationPlaceSelected}
                error={locationError}
                helperText={locationError}
                placeholder="Search address"
              />
              <LocationMapPreview
                height={200}
                latitude={form.latitude}
                longitude={form.longitude}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default DateLocationStep;
